import { Location } from "@/types/tms";
import {
  OrderFormData,
  LegData,
  emptyLeg,
  makeEmptyLeg,
  DEFAULT_WINDOW_MINUTES,
  addMinutesLocal,
} from "@/components/orders/orderFormTypes";

/**
 * Recompute a window end so that `end >= start` and the window length is
 * preserved when possible. Used by all programmatic cascades that rewrite
 * a leg's start time — without this, the previous `_end` is left stale and
 * the form's `end >= start` validator silently disables the submit button.
 *
 *  - If both old values are present and old end > old start, keep that delta.
 *  - Otherwise fall back to `DEFAULT_WINDOW_MINUTES` past the new start.
 */
function syncWindowEnd(oldStart: string, oldEnd: string, newStart: string): string {
  if (!newStart) return oldEnd;
  if (oldStart && oldEnd) {
    const s = new Date(oldStart).getTime();
    const e = new Date(oldEnd).getTime();
    if (!Number.isNaN(s) && !Number.isNaN(e) && e >= s) {
      const deltaMin = Math.round((e - s) / 60_000);
      return addMinutesLocal(newStart, deltaMin);
    }
  }
  return addMinutesLocal(newStart, DEFAULT_WINDOW_MINUTES);
}

/** Minimum spread between a stop's ready time and its delivery time, and the
 *  gap used when seeding a brand-new leg after the previous leg's delivery. */
export const MIN_LEG_GAP_MINUTES = 60;

/** Force `requested_delivery_time` to be at least MIN_LEG_GAP_MINUTES after
 *  `ready_time`, preserving the delivery window length. */
function enforceMinDeliveryGap(leg: LegData): LegData {
  if (!leg.ready_time || !leg.requested_delivery_time) return leg;
  const floor = addMinutesLocal(leg.ready_time, MIN_LEG_GAP_MINUTES);
  if (leg.requested_delivery_time >= floor) return leg;
  return {
    ...leg,
    requested_delivery_time: floor,
    requested_delivery_time_end: syncWindowEnd(
      leg.requested_delivery_time,
      leg.requested_delivery_time_end,
      floor,
    ),
  };
}

/**
 * Push each leg forward so the chain stays chronological:
 *  - a leg's delivery is always >= its ready + 1h;
 *  - a leg's ready is always >= the previous leg's delivery.
 * Times are only ever moved later, never earlier.
 */
export function normalizeLegChain(legs: LegData[], fromIndex = 0): LegData[] {
  const out = [...legs];
  for (let i = Math.max(0, fromIndex); i < out.length; i++) {
    if (i > 0) {
      const prevDelivery = out[i - 1].requested_delivery_time;
      if (prevDelivery && out[i].ready_time && out[i].ready_time < prevDelivery) {
        out[i] = {
          ...out[i],
          ready_time: prevDelivery,
          ready_time_end: syncWindowEnd(out[i].ready_time, out[i].ready_time_end, prevDelivery),
        };
      }
    }
    out[i] = enforceMinDeliveryGap(out[i]);
  }
  return out;
}

type SetFormData = React.Dispatch<React.SetStateAction<OrderFormData>>;

/** Build location field updates for a given direction prefix. */
function locationFields(prefix: "ship_from" | "ship_to", location: Location | null): Partial<LegData> {
  return {
    [`${prefix}_location_id`]: location?.id || "",
    [`${prefix}_street_address`]: location?.street_address || "",
    [`${prefix}_city`]: location?.city || "",
    [`${prefix}_province_state`]: location?.province_state || "",
    [`${prefix}_postal_zip_code`]: location?.postal_zip_code || "",
    [`${prefix}_country`]: location?.country || "",
  } as Partial<LegData>;
}

/** Handle single-leg Ship From location change. */
export function handleShipFromLocationChange(
  setFormData: SetFormData,
  locationId: string | null,
  location: Location | null
) {
  setFormData((prev) => ({
    ...prev,
    ...locationFields("ship_from", location),
    ship_from_location_id: locationId || "",
  }));
}

/** Handle single-leg Ship To location change. Addresses only — times are
 *  never derived from the selected locations. */
export function handleShipToLocationChange(
  setFormData: SetFormData,
  _locations: Location[] | undefined,
  locationId: string | null,
  location: Location | null
) {
  setFormData((prev) => ({
    ...prev,
    ...locationFields("ship_to", location),
    ship_to_location_id: locationId || "",
  }));
}

/** Update a single leg by index. Time edits re-normalize the chain forward so
 *  a later leg never starts before an earlier leg finishes and each leg keeps
 *  at least a one-hour ready→delivery spread. */
export function updateLeg(setFormData: SetFormData, index: number, updates: Partial<LegData>) {
  const touchesTimes =
    "ready_time" in updates ||
    "ready_time_end" in updates ||
    "requested_delivery_time" in updates ||
    "requested_delivery_time_end" in updates;
  setFormData((prev) => {
    const legs = prev.legs.map((leg, i) => (i === index ? { ...leg, ...updates } : leg));
    return { ...prev, legs: touchesTimes ? normalizeLegChain(legs, index) : legs };
  });
}

/**
 * Handle multi-leg location change. When Ship To changes, it cascades to the
 * next leg's Ship From. No time is computed from the chosen locations.
 */
export function handleLegLocationChange(
  setFormData: SetFormData,
  _locations: Location[] | undefined,
  formData: OrderFormData,
  index: number,
  direction: "from" | "to",
  locationId: string | null,
  location: Location | null
) {
  const prefix = direction === "from" ? "ship_from" : "ship_to";
  updateLeg(setFormData, index, {
    [`${prefix}_location_id`]: locationId || "",
    [`${prefix}_street_address`]: location?.street_address || "",
    [`${prefix}_city`]: location?.city || "",
    [`${prefix}_province_state`]: location?.province_state || "",
    [`${prefix}_postal_zip_code`]: location?.postal_zip_code || "",
    [`${prefix}_country`]: location?.country || "",
  } as Partial<LegData>);

  // When Ship To changes, cascade to next leg's Ship From
  if (direction === "to" && index + 1 < formData.legs.length) {
    updateLeg(setFormData, index + 1, {
      ship_from_location_id: locationId || "",
      ship_from_street_address: location?.street_address || "",
      ship_from_city: location?.city || "",
      ship_from_province_state: location?.province_state || "",
      ship_from_postal_zip_code: location?.postal_zip_code || "",
      ship_from_country: location?.country || "",
    });
  }
}

/**
 * Cascade a leg's delivery time to the next leg's ready time, preserving the
 * next leg's window length. The next leg's delivery time is left untouched.
 */
export function cascadeDeliveryToNextReady(
  setFormData: SetFormData,
  _locations: Location[] | undefined,
  index: number,
  newDeliveryTime: string
) {
  setFormData((prev) => {
    if (index + 1 >= prev.legs.length) return prev;
    const newLegs = [...prev.legs];
    newLegs[index + 1] = {
      ...newLegs[index + 1],
      ready_time: newDeliveryTime,
      ready_time_end: syncWindowEnd(
        newLegs[index + 1].ready_time,
        newLegs[index + 1].ready_time_end,
        newDeliveryTime,
      ),
    };
    return { ...prev, legs: normalizeLegChain(newLegs, index + 1) };
  });
}

/** Seed a leg's times to start after `prevDelivery` (plus the minimum gap),
 *  keeping the seeded leg's own window lengths. */
function seedLegAfter(leg: LegData, prevDelivery: string | undefined): LegData {
  if (!prevDelivery) return leg;
  const ready = addMinutesLocal(prevDelivery, MIN_LEG_GAP_MINUTES);
  const deliveryMin = leg.ready_time && leg.requested_delivery_time
    ? Math.max(
        MIN_LEG_GAP_MINUTES,
        Math.round(
          (new Date(leg.requested_delivery_time).getTime() -
            new Date(leg.ready_time).getTime()) / 60_000,
        ) || MIN_LEG_GAP_MINUTES,
      )
    : MIN_LEG_GAP_MINUTES;
  const delivery = addMinutesLocal(ready, deliveryMin);
  return {
    ...leg,
    ready_time: ready,
    ready_time_end: syncWindowEnd(leg.ready_time, leg.ready_time_end, ready),
    requested_delivery_time: delivery,
    requested_delivery_time_end: syncWindowEnd(
      leg.requested_delivery_time,
      leg.requested_delivery_time_end,
      delivery,
    ),
  };
}

/** Add a new leg, copying the previous leg's Ship To as Ship From and starting
 *  its window an hour after the previous leg's delivery. */
export function addLeg(setFormData: SetFormData, formData: OrderFormData) {
  setFormData((prev) => {
    const lastLeg = prev.legs[prev.legs.length - 1];
    const newLeg: LegData = seedLegAfter(
      {
        ...makeEmptyLeg(),
        ship_from_location_id: lastLeg?.ship_to_location_id || "",
        ship_from_street_address: lastLeg?.ship_to_street_address || "",
        ship_from_city: lastLeg?.ship_to_city || "",
        ship_from_province_state: lastLeg?.ship_to_province_state || "",
        ship_from_postal_zip_code: lastLeg?.ship_to_postal_zip_code || "",
        ship_from_country: lastLeg?.ship_to_country || "",
      },
      lastLeg?.requested_delivery_time,
    );
    return { ...prev, legs: normalizeLegChain([...prev.legs, newLeg], prev.legs.length) };
  });
}

/** Remove a leg by index. */
export function removeLeg(setFormData: SetFormData, index: number) {
  setFormData((prev) => ({ ...prev, legs: prev.legs.filter((_, i) => i !== index) }));
}

// ──────────────────────────────────────────────────────────────────────────
// Per-leg HOS / asset validation (Phase 4)
//
// Once `use_same_assets_for_all_legs === false`, each leg carries its own
// tractor + operator(s). The form previously only validated address/time
// completeness per leg. These checks add the cross-leg invariants that the
// fleet-scheduler engine assumes when chaining work onto an asset.
//
// All checks are pure (no DB reads) — they catch data-entry mistakes before
// the order is written. Engine-level constraints (existing assignments on
// other orders, days off, etc.) are still enforced server-side.
// ──────────────────────────────────────────────────────────────────────────

/** US DOT HOS limits used by the scheduler engine. Mirror of
 *  `MAX_SERVICE_HOURS` / `MAX_DUTY_HOURS` in `fleet-scheduler.ts`.
 *  Retained for reference; the order form no longer warns on them. */
export const HOS_MAX_DRIVING_HOURS = 11;
export const HOS_MAX_DUTY_HOURS = 14;


function intervalsOverlap(a: LegData, b: LegData): boolean {
  const s1 = new Date(a.ready_time).getTime();
  const e1 = new Date(a.requested_delivery_time).getTime();
  const s2 = new Date(b.ready_time).getTime();
  const e2 = new Date(b.requested_delivery_time).getTime();
  if ([s1, e1, s2, e2].some((n) => Number.isNaN(n))) return false;
  return s1 < e2 && s2 < e1;
}




/** Cross-leg temporal consistency: a leg must not start before the
 *  previous leg's requested delivery completes. */
function validateLegChronology(legs: LegData[]): string[] {
  const errors: string[] = [];
  for (let i = 1; i < legs.length; i++) {
    const prev = legs[i - 1];
    const curr = legs[i];
    if (!prev.requested_delivery_time || !curr.ready_time) continue;
    if (new Date(curr.ready_time).getTime() < new Date(prev.requested_delivery_time).getTime()) {
      errors.push(
        `Leg ${i + 1}: ready time is before leg ${i}'s delivery time.`,
      );
    }
  }
  return errors;
}

/** Per-leg asset uniqueness: when per-leg pickers are active, the same
 *  tractor or operator cannot be on two legs whose windows overlap. */
function validateLegAssetConflicts(legs: LegData[]): string[] {
  const errors: string[] = [];
  for (let i = 0; i < legs.length; i++) {
    for (let j = i + 1; j < legs.length; j++) {
      const a = legs[i];
      const b = legs[j];
      if (!intervalsOverlap(a, b)) continue;

      if (a.assigned_tractor_id && a.assigned_tractor_id === b.assigned_tractor_id) {
        errors.push(
          `Legs ${i + 1} and ${j + 1}: same tractor assigned to overlapping time windows.`,
        );
      }

      const opsA = [a.assigned_operator_id, a.assigned_operator_2_id].filter(Boolean);
      const opsB = [b.assigned_operator_id, b.assigned_operator_2_id].filter(Boolean);
      const sharedOp = opsA.find((id) => id && opsB.includes(id));
      if (sharedOp) {
        errors.push(
          `Legs ${i + 1} and ${j + 1}: same operator assigned to overlapping time windows.`,
        );
      }
    }

    // Within a single leg, the secondary operator can't equal the primary.
    const leg = legs[i];
    if (
      leg.assigned_operator_id &&
      leg.assigned_operator_2_id &&
      leg.assigned_operator_id === leg.assigned_operator_2_id
    ) {
      errors.push(`Leg ${i + 1}: primary and secondary operator are the same person.`);
    }
  }
  return errors;
}

/**
 * Validate every per-leg invariant the form is responsible for. Returns an
 * empty array when the multi-leg form is valid (or when the form is in
 * single-leg / shared-assets mode, in which case the order-level pickers
 * apply and per-leg asset checks are skipped).
 *
 * The chronology + HOS checks always run on multi-leg orders since they
 * govern the leg windows themselves, regardless of which assets are picked.
 */
export function validateLegs(formData: OrderFormData): string[] {
  if (formData.order_type !== "multi-leg") return [];
  const legs = formData.legs ?? [];
  if (legs.length < 2) return [];

  const errors: string[] = [];

  // Chronology always applies.
  errors.push(...validateLegChronology(legs));


  // Per-leg asset conflicts only matter when the form is in per-leg mode.
  if (formData.use_same_assets_for_all_legs === false) {
    errors.push(...validateLegAssetConflicts(legs));
  }

  return errors;
}
